import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, Users, Target, Sparkles, CheckCircle2, BookOpen, Award, TrendingUp, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import LeadForm from '@/components/shared/LeadForm';

const PROGRAMS = [
  {
    id: 'leadership-speedometer',
    icon: Award,
    title: 'Leadership Development Programme',
    tagline: 'Leadership Speedometer - Fostering Leadership Excellence',
    description: 'Research-based leadership programs that help leaders embrace their roles as visionaries who inspire and empower. Built on the F1 Racer metaphor - controlling speed for perfect thrust.',
    components: [
      { name: 'Lead Self', focus: 'Self-awareness and personal leadership' },
      { name: 'Lead Teams', focus: 'For leaders of individual contributors' },
      { name: 'Lead Managers', focus: 'For leaders of managers' },
      { name: 'Lead Organisation', focus: 'For leaders of the enterprise' }
    ],
    keyPrograms: [
      { title: 'Why Should Anyone Be Led by You?', framework: 'CASE Framework (Community, Authenticity, Significance, Excitement)', detail: 'Practical approach for leaders to understand follower needs and re-evaluate leadership style based on research by Rob Goffee & Gareth Jones' },
      { title: 'Leading Today\'s Professionals+', framework: 'For New Managers', detail: 'Transform from team player to team leader. Equips managers with skills for leading smart, highly skilled knowledge workers based on two decades of research' },
      { title: 'Leading Out Loud', framework: 'Leadership Communication', detail: 'Reflective and practical leadership communication based on Terry Pearce\'s book' },
      { title: 'Helping Others Succeed®', framework: 'CLEAR Model', detail: 'Systematic process of feedback, analysis, planning, and action for leaders to coach effectively' }
    ],
    outcomes: 'Inspiring leaders, empowering vision, stronger bonds with employees and stakeholders, improved leadership capabilities',
    caseStudy: { client: 'Insurance Company', result: '250 leaders trained over 1.5 years, high team energy uplift' }
  },
  {
    id: 'management-development',
    icon: Target,
    title: 'Management Development Programme',
    tagline: 'Execution Excellence Pit Stops',
    description: 'Unlock managerial potential for organizational impact. 12-18 month engagement focusing on mastering Execution Excellence, balancing self-development versus team needs.',
    components: [
      { name: 'Tactical Pit Stops', focus: 'Foundational courses for managerial competence' },
      { name: 'Strategic Pit Stops', focus: 'Mandatory programs for leadership competencies' }
    ],
    keyPrograms: [
      { title: 'Manage Self and Others', framework: 'Foundational Leadership', detail: 'Prepare leaders for increased span of control, player-coach transition, challenging team dynamics' },
      { title: 'Engage Hearts and Ignite Minds', framework: 'Team Energy', detail: 'Create environment for ideas/feedback, deliver on organizational needs, energize team members, leverage talents' },
      { title: 'Build High Performance Teams', framework: 'Team Dynamics', detail: 'Build awareness (Team Collaboration Inventory), create Team Charter, understand team development, sustain momentum' }
    ],
    outcomes: 'Enhanced leadership and managerial skills, improved team performance, better engagement, bench strength development',
    caseStudy: { client: 'Plastics Processor', result: '₹1.95 Cr annual savings, 86% waste reduction, 11 leaders developed' }
  },
  {
    id: 'outthinker',
    icon: Sparkles,
    title: 'The Outthinker Process',
    tagline: 'IDEAS Framework - Unleashing Innovation Power',
    description: 'Equip leaders and teams with a practical, repeatable method to develop and adjust innovative strategy. Based on "Outthink the Competition" by Kaihan Krippendorff.',
    components: [
      { name: 'Imagine', focus: 'Future scenarios and possibilities' },
      { name: 'Dissect', focus: 'Challenge analysis and breakdown' },
      { name: 'Expand', focus: 'Creative solution generation' },
      { name: 'Analyze', focus: 'Strategic evaluation and refinement' },
      { name: 'Sell', focus: 'Buy-in and execution planning' }
    ],
    keyPrograms: [
      { title: 'Strategic Innovation Workshop', framework: 'IDEAS in Action', detail: 'Participants use set of tools applicable to any challenge, working through the complete IDEAS framework' },
      { title: 'Competitive Strategy Development', framework: 'Outthinking Competition', detail: 'Draw on studies of innovative thinkers and companies to develop cohesive strategies' }
    ],
    outcomes: 'Strategic clarity, innovative solutions, cohesive strategy development, driving innovative thinking, competitive advantage',
    caseStudy: { client: 'Multiple Organizations', result: 'Breakthrough strategies, market differentiation, sustained innovation' }
  },
  {
    id: 'competency-programs',
    icon: TrendingUp,
    title: 'Amplify: Competency Based Programs',
    tagline: 'Build Organizational Capability',
    description: 'Develop and demonstrate behaviors showcasing knowledge, skill, abilities, personal qualities, and traits for effective performance. Linked to growth and productivity.',
    components: [
      { name: 'Individual Competencies', focus: 'Emotional Intelligence, Innovation & Creativity, Problem Solving' },
      { name: 'Interpersonal Competencies', focus: 'Collaboration & Teamwork, Conflict Management, Communication' },
      { name: 'Leadership Competencies', focus: 'Building Effective Teams, Delegation, Coaching & Mentoring' },
      { name: 'Organizational Competencies', focus: 'Manage Change, Customer Service, Strategic Thinking' }
    ],
    keyPrograms: [
      { title: 'Participatory Learning', framework: 'Adult Learning Approach', detail: 'Pre-work, Assessment, ILT, Projects, Escape rooms, Individual activities, Role plays, Case Studies' },
      { title: 'Experiential Development', framework: 'Practical Application', detail: 'Personalized Coaching, Action planning, Real-world simulations' }
    ],
    outcomes: 'Improved individual task execution, better management of diverse tasks, effective response to contingencies, enhanced proactivity',
    caseStudy: { client: 'Multiple Organizations', result: 'Enhanced performance across all competency levels' }
  },
  {
    id: 'executive-coaching',
    icon: Users,
    title: 'Executive Coaching',
    tagline: 'Elevate Your Leadership Performance',
    description: 'Build leader capability to achieve short and long-term organizational goals through one-on-one interactions driven by data from multiple perspectives, based on mutual trust and respect.',
    components: [
      { name: 'Assessment', focus: 'Personality and cognitive measures' },
      { name: 'Feedback', focus: '360-degree feedback for strengths and development opportunities' },
      { name: 'Development Planning', focus: 'Specific steps to improve performance' }
    ],
    keyPrograms: [
      { title: '7 Phase GCPS Model', framework: 'Guidance & Coaching Positioning System', detail: 'Scoping → Determining Outcomes → Matching Client with Coach → Gathering Information → Creating Development Strategy → Measuring Results → Closing Process' },
      { title: 'Individual Development Plans', framework: 'Customized Growth', detail: 'Personalized coaching based on assessment data and organizational needs' }
    ],
    outcomes: 'Elevated leadership performance, measurable behavioral change, achievement of organizational goals, potential realization',
    caseStudy: { client: 'Diamond Jewellery Manufacturer', result: '20 leaders upskilled, 2 promoted to CXO in 9 months' }
  },
  {
    id: 'anexi-galaxy',
    icon: BookOpen,
    title: 'Anexi Galaxy (E-Learning)',
    tagline: 'Infinite Learning Opportunities',
    description: 'Empower employees to upskill with eLearning. Goes beyond formal training by bringing social learning and content curation via LMS to drive learning and productivity.',
    components: [
      { name: 'Self-Paced Learning', focus: 'Via HR Anexi App, anytime learning, ownership of self-learning' },
      { name: 'Learning Journeys', focus: 'Customized journeys for higher engagement' },
      { name: 'Learning Nuggets', focus: 'Bite-sized for quick follow-up and recap' },
      { name: 'Gamification', focus: 'Quizzes, graded courses, leaderboards, points, badges' },
      { name: 'Real-World Application', focus: 'Job simulations, case studies, scenario-based learning' }
    ],
    keyPrograms: [
      { title: 'Customized Learning Paths', framework: 'eLearning & Video', detail: 'Tailored journeys based on organizational competencies and individual needs' },
      { title: 'Gamified Content', framework: 'Engagement & Retention', detail: 'Points, badges, leaderboards to drive continuous learning' }
    ],
    outcomes: 'Continuous learning, improved engagement, knowledge retention, real-world application of skills',
    caseStudy: { client: 'Multiple Organizations', result: 'Higher employee engagement, continuous upskilling, improved productivity' }
  }
];

export default function LearningPage() {
  const [expandedProgram, setExpandedProgram] = useState(null);

  return (
    <div className="bg-[#FAF5EF]">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-[#1A1008] to-[#3D1C11] text-white">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#E8642C] rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="max-w-[900px]">
            <p className="text-[11px] font-semibold text-[#E8642C] tracking-[0.25em] uppercase mb-6">Learning & Development</p>
            <h1 className="text-[clamp(3rem,6vw,5.5rem)] font-bold leading-[1.05] tracking-[-0.02em] mb-8 text-white">
              Build leaders.<br/>Build capability.<br/>Build the future.
            </h1>
            <p className="text-xl text-white/80 leading-relaxed mb-12">
              Research-backed leadership programs, competency development, executive coaching, and e-learning solutions that transform individuals and organizations.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#E8642C] text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-[#CC2929] transition-all shadow-lg">
                Explore Programs
                <ArrowRight size={16} />
              </Link>
              <Link to="/impact" className="inline-flex items-center gap-2 bg-white/10 text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-white/20 transition-all border border-white/20">
                View Impact Stories
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1A1008] mb-6">Our Programs</h2>
            <p className="text-lg text-[#6B5B50] max-w-[700px] mx-auto">
              Six comprehensive learning solutions. Click to explore each program in detail.
            </p>
          </div>

          <div className="space-y-6">
            {PROGRAMS.map((program, i) => (
              <DetailedProgramCard 
                key={program.id} 
                program={program} 
                index={i}
                isExpanded={expandedProgram === program.id}
                onToggle={() => setExpandedProgram(expandedProgram === program.id ? null : program.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#FAF5EF]">
        <div className="mx-auto w-full max-w-[1000px] px-6 sm:px-8 lg:px-12">
          <div className="bg-white rounded-3xl p-10 lg:p-16 shadow-xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1008] mb-6">Build your leadership capability</h2>
              <p className="text-lg text-[#6B5B50] max-w-[600px] mx-auto">Let's discuss which programs can accelerate your organizational growth.</p>
            </div>
            <LeadForm source="learning_page" />
          </div>
        </div>
      </section>
    </div>
  );
}

function DetailedProgramCard({ program, index, isExpanded, onToggle }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  const Icon = program.icon;
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="bg-white rounded-3xl border border-[#3D1C11]/10 hover:border-[#E8642C]/30 transition-all overflow-hidden"
    >
      <button
        onClick={onToggle}
        className="w-full p-8 lg:p-10 flex items-start gap-6 text-left hover:bg-[#FAF5EF]/50 transition-colors"
      >
        <div className="flex-shrink-0 w-14 h-14 bg-[#E8642C]/10 rounded-2xl flex items-center justify-center">
          <Icon className="w-7 h-7 text-[#E8642C]" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold text-[#1A1008] mb-2">{program.title}</h3>
              <p className="text-sm text-[#E8642C] font-medium mb-3">{program.tagline}</p>
              <p className="text-[#6B5B50] leading-relaxed">{program.description}</p>
            </div>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="flex-shrink-0"
            >
              <ChevronDown className="w-6 h-6 text-[#6B5B50]" />
            </motion.div>
          </div>
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="px-8 lg:px-10 pb-10 pt-4 border-t border-[#3D1C11]/10 bg-[#FAF5EF]/30">
              <div className="grid lg:grid-cols-2 gap-10">
                {/* Components */}
                <div>
                  <h4 className="text-lg font-bold text-[#1A1008] mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Program Components
                  </h4>
                  <div className="space-y-3">
                    {program.components.map((comp, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#E8642C] flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-bold text-[#1A1008] text-sm">{comp.name}</div>
                          <div className="text-xs text-[#6B5B50]">{comp.focus}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {program.caseStudy && (
                    <div className="mt-8 bg-white rounded-xl p-5 border border-[#E8642C]/20">
                      <div className="text-xs font-bold text-[#1A1008] uppercase tracking-wide mb-2">Case Study</div>
                      <div className="text-sm text-[#6B5B50]">
                        <span className="font-semibold text-[#1A1008]">{program.caseStudy.client}:</span> {program.caseStudy.result}
                      </div>
                    </div>
                  )}
                </div>

                {/* Key Programs */}
                <div>
                  <h4 className="text-lg font-bold text-[#1A1008] mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Key Programs
                  </h4>
                  <div className="space-y-4">
                    {program.keyPrograms.map((kp, i) => (
                      <div key={i} className="bg-white rounded-xl p-4 border border-[#3D1C11]/10">
                        <div className="font-bold text-[#1A1008] text-sm mb-1">{kp.title}</div>
                        <div className="text-xs text-[#E8642C] font-medium mb-2">{kp.framework}</div>
                        <div className="text-xs text-[#6B5B50] leading-relaxed">{kp.detail}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6">
                    <h4 className="text-sm font-bold text-[#1A1008] mb-3">Expected Outcomes</h4>
                    <p className="text-sm text-[#E8642C] font-medium">{program.outcomes}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}